import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Grid,
  GridItem,
  HStack,
  Icon,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { FiList, FiActivity, FiCheckCircle, FiCheck } from 'react-icons/fi';
import ReactApexChart from 'react-apexcharts';
import { Link as RouterLink } from 'react-router-dom';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import {
  UnifiedActionTile,
  UnifiedEmptyState,
  UnifiedLoadingState,
  UnifiedStatCard,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import { getMyListingsDashboard } from 'domains/listing/services/listingService';
import { useGetUserRole } from 'domains/user/hooks/useGetUserRole';
import { isProfileComplete } from 'domains/user/utils/profileCompleteness';
import ProfileWarningBanner from 'domains/user/components/ProfileWarningBanner';
import { useTranslation } from 'i18n/TranslationProvider';
import { apexDefaultOptions, donutDefaultOptions, getGlowOptions } from 'utilities/chartUtils';

const DASHBOARD_DEFAULTS = {
  summary: {
    totalListings: 0,
    activeListings: 0,
    listingsWithQuotes: 0,
    acceptedTransport: 0,
    completedListings: 0,
    canceledListings: 0,
  },
  timeline: {
    categories: [],
    series: [],
  },
  recentListings: [],
};

function formatListingStatus(status, t) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'has_quotes') return t('Has quotes');
  if (normalized === 'awaiting_payment') return t('Awaiting payment');
  if (normalized === 'planned') return t('Planned');
  if (normalized === 'in_transit') return t('In transit');
  if (normalized === 'pickedup') return t('Picked up');
  if (normalized === 'canceled' || normalized === 'expired') return t('Canceled');
  if (normalized === 'open') return t('Open');
  if (normalized === 'accepted') return t('Accepted');
  if (normalized === 'paid') return t('Paid');
  if (normalized === 'delivered') return t('Delivered');
  if (normalized === 'completed') return t('Completed');
  return status || t('Unknown');
}

function getStatusColor(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return 'green';
  if (normalized === 'delivered') return 'teal';
  if (normalized === 'canceled' || normalized === 'expired') return 'red';
  if (normalized === 'planned' || normalized === 'in_transit') return 'cyan';
  if (normalized === 'accepted' || normalized === 'awaiting_payment' || normalized === 'paid') return 'purple';
  if (normalized === 'has_quotes') return 'blue';
  if (normalized === 'open') return 'cyan';
  return 'gray';
}

function formatListingDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function ClientDashboardPage() {
  const { t } = useTranslation();
  const { role, user } = useGetUserRole();
  const [dashboard, setDashboard] = useState(DASHBOARD_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const axisLabelColor = useColorModeValue('#64748B', '#94A3B8');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const showProfileWarning = role === 'client' && !isProfileComplete(user);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMyListingsDashboard();
        if (!cancelled) {
          setDashboard({
            ...DASHBOARD_DEFAULTS,
            ...data,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const timelineChartOptions = useMemo(
    () => ({
      ...apexDefaultOptions,
      ...getGlowOptions('#3965FF'),
      chart: {
        ...apexDefaultOptions.chart,
        type: 'bar',
        stacked: true,
        toolbar: { show: false },
        foreColor: axisLabelColor,
      },
      colors: dashboard.timeline?.series?.length > 0 
        ? dashboard.timeline.series.map((series) => series.color)
        : ['#3965FF', '#7551FF', '#422AFB', '#02044A'],
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          opacityFrom: 0.85,
          opacityTo: 0.95,
          stops: [0, 100],
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '45%',
          dataLabels: {
            total: {
              enabled: true,
              style: {
                fontSize: '11px',
                fontWeight: 700,
                color: '#64748B',
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      stroke: { width: 2, colors: ['transparent'] },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        fontWeight: 500,
        labels: {
          colors: axisLabelColor,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
        markers: {
          width: 8,
          height: 8,
          radius: 12,
        },
      },
      xaxis: {
        ...apexDefaultOptions.xaxis,
        categories: dashboard.timeline?.categories || [],
      },
      yaxis: {
        ...apexDefaultOptions.yaxis,
        min: 0,
        forceNiceScale: true,
        labels: {
          ...apexDefaultOptions.yaxis.labels,
          formatter: (value) => `${Math.round(value)}`,
        },
      },
      tooltip: {
        ...apexDefaultOptions.tooltip,
        shared: true,
        intersect: false,
        y: {
          formatter: (value) => t('{{value}} listings', { value }),
        },
      },
    }),
    [axisLabelColor, dashboard.timeline, t],
  );

  const categoryChartOptions = useMemo(
    () => ({
      ...donutDefaultOptions,
      ...getGlowOptions('#7551FF'),
      labels: Object.keys(dashboard.categoryDistribution || {}).map(k => t(k.charAt(0).toUpperCase() + k.slice(1))),
      colors: ['#3965FF', '#7551FF', '#422AFB', '#02044A', '#707EAE', '#FFB547', '#01B574'],
    }),
    [dashboard.categoryDistribution, t],
  );

  const timelineChartSeries = useMemo(
    () =>
      (dashboard.timeline?.series || []).map((series) => ({
        name: t(series.name),
        data: series.data,
      })),
    [dashboard.timeline, t],
  );

  if (loading) {
    return (
      <PageShell>
        <UnifiedLoadingState label={t('Loading dashboard...')} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          description={t(
            'Overview of your current transport listings, quote activity, and recent requests.',
          )}
          actions={
            <AiAssistantDrawer
              role="client"
              screenContext={{
                routeKey: 'client.home',
                role: 'client',
                surface: 'client-dashboard',
              }}
              buttonLabel={t('Create with AI')}
              subtitle={t('Describe the shipment you need and review the prepared request before publishing.')}
            />
          }
        />

        {showProfileWarning && <ProfileWarningBanner user={user} />}

        {error ? (
          <UnifiedSurfaceCard p={5}>
            <Text color="red.500">{error}</Text>
          </UnifiedSurfaceCard>
        ) : null}

        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4}>
          <UnifiedStatCard 
            label={t('Total listings')} 
            value={dashboard.summary.totalListings} 
            icon={FiList} 
            accent="blue.500" 
            trend={dashboard.summary.listingTrend}
            trendLabel={t('vs last month')}
          />
          <UnifiedStatCard label={t('Active listings')} value={dashboard.summary.activeListings} icon={FiActivity} accent="orange.500" />
          <UnifiedStatCard label={t('Completed')} value={dashboard.summary.completedListings} icon={FiCheck} accent="teal.500" />
          <UnifiedStatCard label={t('Total spend')} value={`€${Number(dashboard.summary.totalSpending || 0).toLocaleString()}`} icon={FiCheckCircle} accent="purple.500" />
        </SimpleGrid>

        {dashboard.summary.listingTrend > 0 && (
          <UnifiedSurfaceCard p={4} variant="glow" bg="blue.50/30">
            <HStack spacing={3}>
              <Icon as={FiActivity} color="blue.500" />
              <VStack align="start" spacing={0}>
                <Text fontWeight="700" size="sm">{t('Smart Analytics')}</Text>
                <Text fontSize="sm" color={mutedText}>
                  {t('Your listing activity grew by {{percent}}% this month. Great job expanding your transport reach!', { percent: dashboard.summary.listingTrend })}
                </Text>
              </VStack>
            </HStack>
          </UnifiedSurfaceCard>
        )}

        <UnifiedSurfaceCard p={5}>
          <VStack align="stretch" spacing={3}>
            <Text fontWeight="700" fontSize="lg">
              {t('Listing status timeline')}
            </Text>
            <Text color={mutedText} fontSize="sm">
              {t('Last 6 months of listings grouped by current status.')}
            </Text>
            <Box h={{ base: '300px', md: '320px' }}>
              {timelineChartSeries.every((series) => series.data.every((value) => value === 0)) ? (
                <UnifiedEmptyState
                  title={t('No listing activity yet')}
                  description={t('Create your first listing to start tracking request activity here.')}
                  minH="100%"
                />
              ) : (
                <ReactApexChart
                  options={timelineChartOptions}
                  series={timelineChartSeries}
                  type="bar"
                  height="100%"
                />
              )}
            </Box>
          </VStack>
        </UnifiedSurfaceCard>

        <UnifiedSurfaceCard p={5}>
          <Text fontWeight="700" fontSize="lg" mb={4}>
            {t('Cargo breakdown')}
          </Text>
          <Box h={{ base: '300px', md: '320px' }}>
             <ReactApexChart
               options={categoryChartOptions}
               series={Object.values(dashboard.categoryDistribution || {})}
               type="donut"
               height="100%"
             />
          </Box>
        </UnifiedSurfaceCard>

        <Grid templateColumns={{ base: '1fr', xl: '1.2fr 1fr' }} gap={4}>
          <GridItem>
            <UnifiedSurfaceCard p={5}>
              <Text fontWeight="700" fontSize="lg" mb={4}>
                {t('Quick actions')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <UnifiedActionTile
                  title={t('Create new listing')}
                  description={t('Create a new transport request for the marketplace.')}
                  to="/create-listing"
                />
                <UnifiedActionTile
                  title={t('View my listings')}
                  description={t('Review quote activity and manage your transport requests.')}
                  to="/my-listings"
                />
                <UnifiedActionTile
                  title={t('Open profile')}
                  description={t('Update company and contact details.')}
                  to="/profile"
                />
              </SimpleGrid>
            </UnifiedSurfaceCard>
          </GridItem>

          <GridItem>
            <UnifiedSurfaceCard p={5}>
              <HStack justify="space-between" mb={4}>
                <Text fontWeight="700" fontSize="lg">
                  {t('Recent listings')}
                </Text>
                <Badge colorScheme="brand">{dashboard.recentListings.length}</Badge>
              </HStack>
              {dashboard.recentListings.length === 0 ? (
                <UnifiedEmptyState
                  title={t('No listings yet')}
                  description={t('Your newest transport requests will appear here once you create them.')}
                  minH="220px"
                />
              ) : (
                <VStack align="stretch" spacing={3}>
                  {dashboard.recentListings.map((listing) => {
                    const listingId = listing._id || listing.id;
                    const fromLabel = [listing.fromCity, listing.fromCountry].filter(Boolean).join(', ') || t('Unknown origin');
                    const toLabel = [listing.toCity, listing.toCountry].filter(Boolean).join(', ') || t('Unknown destination');

                    return (
                      <LinkBox
                        key={listingId}
                        as={UnifiedSurfaceCard}
                        p={3}
                      >
                        <LinkOverlay
                          as={RouterLink}
                          to={`/my-listings/${listingId}`}
                          fontWeight="700"
                          color="brand.500"
                        >
                          {listing.title || listing.humanId || (listingId ? String(listingId).slice(-6).toUpperCase() : t('Listing'))}
                        </LinkOverlay>
                        <Text fontSize="sm" color={mutedText} mt={1}>
                          {fromLabel} → {toLabel}
                          {listing.distanceKm ? ` · ${listing.distanceKm} km` : ''}
                        </Text>
                        <HStack justify="space-between" mt={2} flexWrap="wrap">
                          <HStack spacing={2}>
                            <Badge colorScheme={getStatusColor(listing.status)}>
                              {formatListingStatus(listing.status, t)}
                            </Badge>
                            <Badge colorScheme="brand" variant="subtle">
                              {t('{{count}} quote(s)', { count: listing.quoteCount || 0 })}
                            </Badge>
                          </HStack>
                          <Text fontSize="xs" color={mutedText}>
                            {formatListingDate(listing.createdAt)}
                          </Text>
                        </HStack>
                      </LinkBox>
                    );
                  })}
                </VStack>
              )}
            </UnifiedSurfaceCard>
          </GridItem>
        </Grid>
      </VStack>
    </PageShell>
  );
}
